/**
 * "Resources": the approved protocol library, and the treatment catalogue.
 *
 * The client's sidebar has a Resources entry and no visual for it. The thing a
 * clinician actually reaches for mid-consultation is the dosing schedule, so
 * that is what this is: the GLO MWL protocol phases, timelines and
 * instructions, read straight from the therapy library the matching engine
 * uses.
 *
 * Served rather than transcribed. When GLO issues a new library version the
 * page changes with it, and nobody has to remember that a copy exists in the
 * front end. The caption above each protocol is the library's own.
 *
 * An administrator can maintain what their clinic offers from here. That is
 * narrow on purpose and the tab explains its own limits: the library stays the
 * clinical authority, and nothing on this screen can invent a therapy or
 * rename one.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  ConfirmDialog,
  Disclosure,
  EmptyState,
  ErrorState,
  Field,
  InfoHint,
  LoadingRows,
  PageHeader,
  Pill,
  Toggle,
  inputClass,
} from '../components/ui';

interface ProtocolPhase {
  phase: string;
  timeline: string | null;
  dose: string | null;
  units: string | null;
  instruction: string | null;
}

interface Protocol {
  protocolId: string;
  medication: string;
  pathway: string | null;
  phases: ProtocolPhase[];
}

interface ProtocolLibrary {
  version: string;
  caption: string;
  disclaimer: string;
  protocols: Protocol[];
}

interface Treatment {
  id: string;
  therapyId: string;
  name: string;
  category: string;
  class: string | null;
  route: string | null;
  protocolReference: string | null;
  useOrIndication: string | null;
  goalTags: string[];
  isActive: boolean;
  libraryVersion: string;
}

export function ResourcesPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'protocols' | 'catalogue'>('protocols');

  return (
    <>
      <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5b6b7c]" data-testid="library-header">
        MCOS Protocol Library · clinical reference for licensed clinicians · not a recommendation
      </p>
      <PageHeader
        title="Resources"
        subtitle="Approved dosing protocols, and the treatments this clinic offers."
      />

      <div className="mb-5 flex gap-1.5">
        <button
          type="button"
          onClick={() => setTab('protocols')}
          className={`rounded-control border px-3.5 py-2 text-xs font-semibold transition ${
            tab === 'protocols'
              ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
              : 'border-ink/15 text-ink-muted hover:text-ink'
          }`}
        >
          Protocols
        </button>
        <button
          type="button"
          onClick={() => setTab('catalogue')}
          className={`rounded-control border px-3.5 py-2 text-xs font-semibold transition ${
            tab === 'catalogue'
              ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
              : 'border-ink/15 text-ink-muted hover:text-ink'
          }`}
        >
          Treatment catalogue
        </button>
      </div>

      {tab === 'protocols' ? <Protocols /> : <Catalogue canEdit={can('ADMIN')} />}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Protocols
 * ------------------------------------------------------------------ */

function Protocols() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['protocols'],
    queryFn: () => api.get<ProtocolLibrary>('/treatments/protocols'),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={6} />;

  if (data.protocols.length === 0) {
    return (
      <EmptyState
        title="No protocols in this library version"
        body="The therapy library GLO supplied carries no dosing schedules. Ask for the protocol tab to be included in the next version."
      />
    );
  }

  return (
    <>
      <p className="mb-4 text-xs text-ink-muted">
        {data.caption} · Library version {data.version}
      </p>

      <div className="space-y-3">
        {data.protocols.map((protocol) => (
          <Card key={protocol.protocolId} padded={false}>
            <Disclosure
              title={protocol.medication}
              summary={[protocol.pathway, `${protocol.phases.length} phases`]
                .filter(Boolean)
                .join(' · ')}
            >
              <ol className="divide-y divide-ink/5">
                {protocol.phases.map((phase, index) => (
                  <li key={`${phase.phase}-${index}`} className="flex flex-wrap gap-4 px-5 py-3">
                    <span className="w-40 shrink-0">
                      <span className="block text-sm font-semibold text-ink">{phase.phase}</span>
                      {phase.timeline && (
                        <span className="block text-xs text-ink-muted">{phase.timeline}</span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      {phase.dose && (
                        <span className="block text-sm font-semibold text-ink">
                          {phase.dose}
                          {phase.units ? ` ${phase.units}` : ''}
                        </span>
                      )}
                      {phase.instruction && (
                        <span className="block text-xs text-ink-muted">{phase.instruction}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </Disclosure>
          </Card>
        ))}
      </div>

      <p className="mt-5 text-[11px] leading-relaxed text-ink-muted">{data.disclaimer}</p>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * The treatment catalogue
 * ------------------------------------------------------------------ */

function Catalogue({ canEdit }: { canEdit: boolean }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['treatments', 'all'],
    queryFn: () => api.get<{ treatments: Treatment[] }>('/treatments?includeInactive=1'),
  });
  const [editing, setEditing] = useState<Treatment | null>(null);

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;
  if (isLoading || !data) return <LoadingRows rows={8} />;

  const active = data.treatments.filter((treatment) => treatment.isActive);
  const retired = data.treatments.filter((treatment) => !treatment.isActive);

  return (
    <>
      <Card className="mb-4">
        <p className="text-sm text-ink-muted">
          {active.length} offered, {retired.length} retired. The therapy library is the clinical
          authority: a treatment cannot be added, renamed or deleted here.{' '}
          <InfoHint label="Why not">
            A therapy&apos;s id and name appear on every regimen ever recorded with it. Renaming one
            would silently rewrite what past patients were told they were given, so retiring is the
            only way to take something off a clinician&apos;s panel, and the record stays readable.
          </InfoHint>
        </p>
      </Card>

      <Card padded={false}>
        <ul className="divide-y divide-ink/5">
          {data.treatments.map((treatment) => (
            <li key={treatment.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-ink">{treatment.name}</span>
                  <Pill className="bg-ink/5 text-ink-muted">{treatment.category}</Pill>
                  {!treatment.isActive && (
                    <Pill className="bg-[var(--amber-soft)] text-amber-deep">Retired</Pill>
                  )}
                </span>
                {treatment.useOrIndication && (
                  <span className="mt-0.5 block text-sm text-ink-muted">
                    {treatment.useOrIndication}
                  </span>
                )}
                <span className="mt-1 block text-xs text-ink-muted">
                  {[treatment.route, treatment.protocolReference].filter(Boolean).join(' · ') ||
                    'No route recorded'}
                </span>
                {treatment.goalTags.length > 0 && (
                  <span className="mt-1.5 flex flex-wrap gap-1">
                    {treatment.goalTags.map((tag) => (
                      <Pill key={tag} className="bg-[var(--sage-soft)] text-sage-deep">
                        {tag}
                      </Pill>
                    ))}
                  </span>
                )}
              </span>

              {canEdit && (
                <Button variant="secondary" size="sm" onClick={() => setEditing(treatment)}>
                  Edit
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {editing && (
        <EditTreatment
          treatment={editing}
          onDone={() => {
            setEditing(null);
            void refetch();
          }}
        />
      )}
    </>
  );
}

function EditTreatment({ treatment, onDone }: { treatment: Treatment; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [route, setRoute] = useState(treatment.route ?? '');
  const [protocolReference, setProtocolReference] = useState(treatment.protocolReference ?? '');
  const [useOrIndication, setUseOrIndication] = useState(treatment.useOrIndication ?? '');
  const [isActive, setIsActive] = useState(treatment.isActive);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goals = useQuery({
    queryKey: ['goal-vocabulary'],
    queryFn: () => api.get<{ goals: string[]; units: string[] }>('/treatments/goals'),
  });
  const [goalTags, setGoalTags] = useState<string[]>(treatment.goalTags);

  const save = useMutation({
    mutationFn: () =>
      api.patch<Treatment>(`/treatments/${treatment.id}`, {
        route: route.trim() || null,
        protocolReference: protocolReference.trim() || null,
        useOrIndication: useOrIndication.trim() || null,
        isActive,
        goalTags,
      }),
    onSuccess: () => {
      // The treatment panel reads this list, so its cache has to go too or a
      // clinician keeps seeing a therapy an administrator just retired.
      void queryClient.invalidateQueries({ queryKey: ['treatments'] });
      onDone();
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'Could not save'),
  });

  const submit = () => {
    setError(null);
    /*
     * Retiring is confirmed, editing is not. Taking a therapy off the panel
     * changes what every clinician in the practice can prescribe from the next
     * page load, and it is the one action here that is not obviously
     * reversible to the person doing it.
     */
    if (treatment.isActive && !isActive) {
      setConfirmRetire(true);
      return;
    }
    save.mutate();
  };

  return (
    <Card title={treatment.name} subtitle={treatment.therapyId} className="mt-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Route">
          <input className={inputClass} value={route} maxLength={60} onChange={(e) => setRoute(e.target.value)} />
        </Field>
        <Field label="Protocol reference">
          <input
            className={inputClass}
            value={protocolReference}
            maxLength={200}
            onChange={(e) => setProtocolReference(e.target.value)}
          />
        </Field>
      </div>

      <Field label="What it is for" hint="Shown to clinicians on the treatment panel, and to the patient on their plan.">
        <textarea
          className={inputClass}
          rows={2}
          maxLength={500}
          value={useOrIndication}
          onChange={(e) => setUseOrIndication(e.target.value)}
        />
      </Field>

      <Field
        label="Goals it is indicated for"
        hint="A patient whose goal matches one of these sees this treatment in the protocol matches rather than under other options."
      >
        <div className="flex flex-wrap gap-1.5">
          {(goals.data?.goals ?? []).map((goal) => {
            const on = goalTags.includes(goal);
            return (
              <button
                key={goal}
                type="button"
                onClick={() =>
                  setGoalTags(on ? goalTags.filter((tag) => tag !== goal) : [...goalTags, goal])
                }
                className={`rounded-control border px-2.5 py-1 text-xs font-semibold transition ${
                  on
                    ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                    : 'border-ink/15 text-ink-muted hover:text-ink'
                }`}
              >
                {goal}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="mt-4 flex items-center justify-between gap-4 border-t border-ink/5 pt-4">
        <Toggle
          checked={isActive}
          onChange={setIsActive}
          label="Offered by this clinic"
        />
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-[var(--band-risk)]">{error}</p>}

      <ConfirmDialog
        open={confirmRetire}
        title={`Retire ${treatment.name}?`}
        body="No clinician will be able to select it from the next page load. Existing regimens that use it stay readable, and you can offer it again at any time."
        confirmLabel="Retire it"
        onCancel={() => setConfirmRetire(false)}
        onConfirm={() => {
          setConfirmRetire(false);
          save.mutate();
        }}
      />
    </Card>
  );
}
